import type PDFDocument from "@react-pdf/pdfkit";

import { ONE_MM_IN_POINTS } from "../constants/badgePdf";

/**
 * Shortens name if too long by converting last name to initial
 */
export function getBadgeDisplayName(params: {
  firstName: string;
  lastName: string;
  maxLength?: number;
}): string {
  const { firstName, lastName, maxLength = 30 } = params;
  const fullName = `${firstName} ${lastName}`;

  if (fullName.length <= maxLength) {
    return fullName;
  }

  return `${firstName} ${lastName.charAt(0)}.`;
}

/**
 * Convert a distance in millimeters to points (to be used with PDFKit)
 * @param mm - distance in millimeters
 * @returns
 */
export const mmToPoints = (mm: number) => mm * ONE_MM_IN_POINTS;

interface GetOptimalFontSettingsParams {
  pdf: InstanceType<typeof PDFDocument>;
  text: string;
  maxWidth: number;
  maxHeight: number;
  font: string;
  minFontSize: number;
  maxFontSize: number;
  lineGap?: number;
}

/**
 * Calculates optimal font size for text to fit within specified dimensions
 */
export function getOptimalFontSettings(params: GetOptimalFontSettingsParams) {
  const { pdf, text, maxWidth, maxHeight, font, minFontSize, maxFontSize, lineGap = 0 } = params;

  let bestFontSize = minFontSize;

  // Binary search for optimal font size
  let low = minFontSize;
  let high = maxFontSize;

  while (low <= high) {
    const fontSize = Math.floor((low + high) / 2);
    pdf.font(font).fontSize(fontSize);

    const width = pdf.widthOfString(text);
    const height = pdf.heightOfString(text, { width: maxWidth, lineGap });

    if (width <= maxWidth && height <= maxHeight) {
      bestFontSize = fontSize;
      low = fontSize + 1; // Try larger size
    } else {
      high = fontSize - 1; // Try smaller size
    }
  }

  // Recalculate final measurements with the best font size
  const actualHeight = pdf
    .font(font)
    .fontSize(bestFontSize)
    .heightOfString(text, { width: maxWidth, lineGap });

  return {
    fontSize: bestFontSize,
    actualHeight,
  };
}

/**
 * In most of cases firstName and lastName are available in the worker object.
 * But in some cases due to old tech debt they are not available but are present in name field.
 * So we need to split the name and get the firstName and lastName.
 *
 * Ideally we should fix this in DB. For now this is a temporary solution. We have open ticket for backend fix
 */
export function parseWorkerNameForBadgePdf(worker: {
  name: string;
  firstName?: string;
  lastName?: string;
}): {
  firstName: string;
  lastName: string;
} {
  if (worker.firstName?.trim() && worker.lastName?.trim()) {
    return {
      firstName: worker.firstName,
      lastName: worker.lastName,
    };
  }

  const splitName = (worker.name ?? "").trim().split(" ");
  return {
    firstName: splitName[0].trim(),
    lastName: splitName.slice(1).join(" "),
  };
}
